import * as d3 from 'd3';
import { maxBy } from '../../../../shared/utils/arrayUtils.ts';
import { createGraphG, getTextWidth, GraphG, GraphSvg } from '../../../utils/d3Utils.ts';
import { PreparedData } from '../prepare-data/prepareData.ts';
import type { MeasurementGraphSettings } from '../settings/measurementGraphSettings.ts';

type Tooltip = d3.Selection<HTMLDivElement, unknown, d3.BaseType, undefined>;

export interface MGraphComponents {
    graph: GraphG;
    x: d3.ScalePoint<string>;
    y: d3.ScaleLinear<number, number, never>;
    tooltip: Tooltip;
}

export function createComponents(
    container: HTMLDivElement,
    root: GraphSvg,
    preparedData: PreparedData,
    settings: MeasurementGraphSettings = { lineVisibilities: {} },
) {
    const showAxisLabels = settings.showAxisLabels ?? false;
    const margins = showAxisLabels ? { top: 20, right: 23, bottom: 54, left: 68 } : { top: 15, right: 23, bottom: 30, left: 50 };
    const graph = createGraphG(root, margins);
    const { x, y } = createAxes(root, graph, preparedData, showAxisLabels);
    const tooltip = createTooltip(container);

    return {
        root,
        graph,
        x,
        y,
        tooltip,
    };
}

function createAxes(root: GraphSvg, graph: GraphG, { metaData, measurements }: PreparedData, showAxisLabels: boolean) {
    const { width: graphWidth, height: graphHeight, margins: graphMargins } = graph.datum();
    const textSize = getTextSize(+root.attr('width'));

    const labelMargin = 5;
    const allLabels = measurements.map((it) => it.partCountLabel);
    const maxLabel = maxBy(allLabels, (it) => it.length) ?? '';
    const maxLabelWidth = getTextWidth(graph, maxLabel, textSize);
    const maxLabels = Math.floor(graphWidth / (maxLabelWidth + labelMargin));
    const labelFrequency = Math.max(Math.ceil(measurements.length / maxLabels), 1);
    const isLabelVisible = (i: number) => (measurements.length - 1 - i) % labelFrequency === 0; // The last label is always visible

    const x = d3
        .scalePoint()
        .range([0, graphWidth])
        .domain(measurements.map((_, index) => '' + index));
    root.append('g')
        .attr('transform', `translate(${graphMargins.left}, ${graphHeight + graphMargins.top})`)
        .call(d3.axisBottom(x).tickFormat((_, i) => (isLabelVisible(i) ? measurements[i].partCountLabel : '')))
        .selectAll('text')
        .style('font-size', textSize + 'px');

    const y = d3.scaleLinear().range([graphHeight, 0]).domain(metaData.graphLimits);
    root.append('g')
        .attr('transform', `translate(${graphMargins.left}, ${graphMargins.top})`)
        .call(d3.axisLeft(y))
        .selectAll('text')
        .style('font-size', textSize + 'px');

    if (showAxisLabels) {
        const svgHeight = +root.attr('height');

        // X axis title
        root.append('text')
            .attr('x', graphMargins.left + graphWidth / 2)
            .attr('y', svgHeight - 10)
            .attr('text-anchor', 'middle')
            .style('font-size', textSize + 'px')
            .text('Part count');

        // Y axis title
        root.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -(graphMargins.top + graphHeight / 2))
            .attr('y', 14)
            .attr('text-anchor', 'middle')
            .style('font-size', textSize + 'px')
            .text('Value');
    }

    return { x, y };
}

function createTooltip(container: HTMLDivElement): Tooltip {
    let tooltip = d3.select('#tooltip') as Tooltip;
    if (tooltip.empty()) {
        tooltip = d3.select(container)
            .append('div')
            .attr('id', 'tooltip')
            .style('position', 'absolute')
            .style('visibility', 'hidden')
            .style('background-color', 'white')
            .style('padding', '5px')
            .style('border', '1px solid #ccc')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('z-index', '10') //Ensure tooltip is on top of other elements
            .style('top', '100px') //Make sure the initial position is within window bounds
            .style('left', '100px')
            .style('text-align', 'left')
            .style('white-space', 'pre-wrap'); // Ensure text wraps and supports line breaks
    }
    return tooltip;
}

function getTextSize(containerWidth: number): number {
    if (containerWidth < 750) {
        return 10;
    } else {
        return 12;
    }
}
